import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatAppDate } from '../../utils/dateFormat';
import PageHeader from '../../components/PageHeader';
import MobileEntityCard from '../../components/MobileEntityCard';
import YandexOrdImportPanel from '../../components/YandexOrdImportPanel';
import { cabinetInvoiceApi } from '../../services/api';
import type { CabinetInvoice } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const InvoicesPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isYandex = user?.ordProvider === 'YANDEX';
  const [items, setItems] = useState<CabinetInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [detail, setDetail] = useState<CabinetInvoice | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setItems(await cabinetInvoiceApi.list());
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  const syncFromOrd = async () => {
    try {
      setSyncing(true);
      setError(null);
      setInfo(null);
      const n = await cabinetInvoiceApi.syncFromOrd();
      setInfo(`Обновлено номеров из ОРД: ${n}`);
      setItems(await cabinetInvoiceApi.list());
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка синхронизации с ОРД');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await load();
      if (cancelled || isYandex) return;
      // Фоном подтянуть номера — список уже показан
      try {
        setSyncing(true);
        const n = await cabinetInvoiceApi.syncFromOrd();
        if (cancelled) return;
        if (n > 0) {
          setInfo(`Обновлено номеров из ОРД: ${n}`);
          setItems(await cabinetInvoiceApi.list());
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.message || e?.message || 'Не удалось подтянуть номера из ОРД');
        }
      } finally {
        if (!cancelled) setSyncing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isYandex]);

  const money = (v?: number | null) =>
    v == null ? '—' : `${Number(v).toLocaleString('ru-RU')} ₽`;

  const periodLabel = (item: CabinetInvoice) => {
    if (!item.firstInvoicePeriodStart) return '—';
    const start = formatAppDate(item.firstInvoicePeriodStart);
    const end = item.invoicePeriodEnd
      ? formatAppDate(item.invoicePeriodEnd)
      : '—';
    return `${start} — ${end}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Акты"
        description={
          isYandex
            ? 'Сданные акты. У Яндекс ОРД номера подтягиваются по известному id или через Excel.'
            : 'Сданные акты. Номера — поле serial из ОРД (как сейчас в ОРД).'
        }
        actions={
          isYandex ? undefined : (
            <Button variant="outlined" onClick={syncFromOrd} disabled={syncing}>
              {syncing ? 'Тянем из ОРД…' : 'Номера из ОРД'}
            </Button>
          )
        }
      />
      {isYandex && (
        <YandexOrdImportPanel entityType="invoices" onDone={load} />
      )}
      {syncing && !isYandex && (
        <Alert severity="info" sx={{ mb: 1.5 }} icon={<CircularProgress size={18} />}>
          Подтягиваем номера актов из ОРД…
        </Alert>
      )}
      {info && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setInfo(null)}>{info}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      {isMobile ? (
        <Stack spacing={1.5}>
          {items.map((item) => (
            <MobileEntityCard
              key={item.creativeDId}
              title={item.name}
              subtitle={item.invoiceSerial || item.erid || '—'}
              chips={[
                <Chip key="inv" size="small" label="сдан" color="success" />,
              ]}
              meta={[
                { label: 'Контрагент', value: item.counterpartyName || '—' },
                { label: 'Договор', value: item.contractSerial || '—' },
                { label: 'Сумма', value: money(item.priceAmount) },
                { label: 'Период', value: periodLabel(item) },
              ]}
              actions={
                <Button size="small" onClick={() => setDetail(item)}>Открыть</Button>
              }
            />
          ))}
          {items.length === 0 && <Typography color="text.secondary">Нет актов</Typography>}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Номер акта</TableCell>
                <TableCell>Креатив</TableCell>
                <TableCell>Контрагент</TableCell>
                <TableCell>Договор</TableCell>
                <TableCell>Сумма</TableCell>
                <TableCell>Период</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.creativeDId}>
                  <TableCell>
                    <code>{item.invoiceSerial || '—'}</code>
                  </TableCell>
                  <TableCell>
                    {item.name}
                    {item.lastError && (
                      <Typography variant="caption" color="error" display="block">{item.lastError}</Typography>
                    )}
                  </TableCell>
                  <TableCell>{item.counterpartyName || '—'}</TableCell>
                  <TableCell>{item.contractSerial || '—'}</TableCell>
                  <TableCell>{money(item.priceAmount)}</TableCell>
                  <TableCell>{periodLabel(item)}</TableCell>
                  <TableCell>
                    <Chip size="small" label="сдан" color="success" />
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => setDetail(item)}>Открыть</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Акт {detail?.invoiceSerial || ''}
        </DialogTitle>
        <DialogContent>
          {detail && (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Содержимое акта по данным кабинета.
              </Typography>
              <Divider />
              <DetailRow label="Номер акта" value={detail.invoiceSerial || '—'} />
              <DetailRow label="Договор акта" value={detail.contractSerial || '—'} />
              <DetailRow label="Контрагент" value={detail.counterpartyName || '—'} />
              <DetailRow label="Роль заказчика" value={detail.clientRoleLabel || 'Рекламодатель'} />
              <DetailRow label="Роль исполнителя" value={detail.contractorRoleLabel || 'Издатель'} />
              <DetailRow
                label="Дата начала периода"
                value={detail.firstInvoicePeriodStart
                  ? formatAppDate(detail.firstInvoicePeriodStart)
                  : '—'}
              />
              <DetailRow
                label="Дата окончания периода"
                value={detail.invoicePeriodEnd
                  ? formatAppDate(detail.invoicePeriodEnd)
                  : '—'}
              />
              <DetailRow
                label="Дата выставления"
                value={detail.invoiceDate ? formatAppDate(detail.invoiceDate) : '—'}
              />
              <DetailRow label="Сумма (с НДС, если облагается), ₽" value={money(detail.priceAmount)} />
              <DetailRow label="Ставка НДС, %" value={detail.vatRateLabel || 'Без НДС'} />
              <DetailRow label="Сумма НДС, ₽" value="0 ₽" />
              <DetailRow label="Сумма без НДС, ₽" value={money(detail.priceAmount)} />
              <Divider />
              <DetailRow label="Креатив" value={detail.name} />
              <DetailRow label="ERID" value={detail.erid || '—'} />
              {detail.invoiceExternalId && (
                <DetailRow label="ID в ОРД" value={detail.invoiceExternalId} />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2" fontWeight={600}>{value}</Typography>
    </Box>
  );
}

export default InvoicesPage;
